import { useCallback, useEffect, useRef, useState } from 'react';

export const useLocalStorage = <S>(storageKey: string, initialData: S) => {
  const previousKeyRef = useRef(storageKey);
  // Store initialData in a ref so callbacks can access the current value
  // without causing effect re-runs when a new reference is passed (e.g. `[]`).
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  const getStoredValue = useCallback((key: string) => {
    const v = window.localStorage?.getItem(key);
    if (v) {
      try {
        return JSON.parse(v) as S;
      } catch (_error) {
        // Corrupted/unexpected storage value. Remove it so we don't keep
        // parsing and logging on every render in tests.
        window.localStorage?.removeItem(key);
      }
    }
    return initialDataRef.current;
  }, []);

  const [value, setValue] = useState(() => getStoredValue(storageKey));

  // Keep a ref to the current value for migration (avoids value in deps)
  const valueRef = useRef(value);
  valueRef.current = value;

  // Keep a ref to the serialized value so we can avoid redundant work when
  // handling same-tab storage events.
  const serializedValueRef = useRef<string>(JSON.stringify(value));

  // Handle storageKey changes - migrate data from old key to new key if needed
  useEffect(() => {
    const previousKey = previousKeyRef.current;
    if (previousKey !== storageKey) {
      const newKeyValue = window.localStorage?.getItem(storageKey);
      if (!newKeyValue) {
        // New key has no data - migrate current value to new key
        if (window.localStorage) {
          const nextSerialized = JSON.stringify(valueRef.current);
          window.localStorage.setItem(storageKey, nextSerialized);
          serializedValueRef.current = nextSerialized;
        }
      } else {
        // New key has data - use it
        setValue(getStoredValue(storageKey));
        serializedValueRef.current = newKeyValue;
      }
      previousKeyRef.current = storageKey;
    }
  }, [storageKey, getStoredValue]);

  // Persist to localStorage when value changes
  useEffect(() => {
    if (window.localStorage) {
      const oldValue = window.localStorage.getItem(storageKey);
      const newValue = JSON.stringify(value);

      // Avoid expensive sync writes (and event churn) when value didn't change.
      if (oldValue === newValue) {
        serializedValueRef.current = newValue;
        return;
      }

      window.localStorage.setItem(storageKey, newValue);
      serializedValueRef.current = newValue;

      // Dispatch storage event for same-tab synchronization
      // (native StorageEvent only fires for cross-tab changes)
      window.dispatchEvent(
        new StorageEvent('storage', { key: storageKey, oldValue, newValue }),
      );
    }
  }, [value, storageKey]);

  // Listen for storage events from other tabs/windows
  useEffect(() => {
    const storageEventHandler = (event: StorageEvent) => {
      if (event.key !== storageKey) return;

      // Same-tab dispatched events will include newValue; use it to avoid an
      // extra localStorage read + JSON.parse.
      if (typeof event.newValue === 'string') {
        if (event.newValue === serializedValueRef.current) return;
        try {
          setValue(JSON.parse(event.newValue) as S);
          serializedValueRef.current = event.newValue;
        } catch (_error) {
          window.localStorage?.removeItem(storageKey);
          setValue(initialDataRef.current);
          serializedValueRef.current = JSON.stringify(initialDataRef.current);
        }
        return;
      }

      // Cross-tab events: fall back to reading from localStorage.
      const nextSerialized = window.localStorage?.getItem(storageKey);
      if (nextSerialized && nextSerialized === serializedValueRef.current) {
        return;
      }
      setValue(getStoredValue(storageKey));
      if (typeof nextSerialized === 'string') {
        serializedValueRef.current = nextSerialized;
      }
    };
    window.addEventListener('storage', storageEventHandler);
    return () => {
      window.removeEventListener('storage', storageEventHandler);
    };
  }, [storageKey, getStoredValue]);

  return [value, setValue] as const;
};
